import {
  IFoundLyrics,
  ITokenExpiryPair,
  IWordMeaningRequest,
  IWordMeaningResponse
} from "@shared/dto";
import Config from "../config";

const baseURL = Config.api.root;

export function getLyrics(
  artists: string[],
  title: string,
  albumName: string,
  duration: number,
  originalTrackName?: string,
  spotifyId?: string
): Promise<IFoundLyrics | null> {
  const parameters = new URLSearchParams();
  parameters.append("title", title);
  for (const artist of artists) {
    parameters.append("artists", artist);
  }
  parameters.append("albumName", albumName);
  parameters.append("duration", duration.toString());
  parameters.append("originalTrackName", originalTrackName ?? "");
  parameters.append("spotifyId", spotifyId ?? "");

  return fetch(`${baseURL}/api/lyrics?${parameters}`).then(r =>
    r.status === 200 ? r.json() : null
  );
}

export async function getWordMeaning(request: IWordMeaningRequest): Promise<IWordMeaningResponse> {
  const response = await fetch(`${baseURL}/api/meaning`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    throw new Error("Cannot load meaning");
  }

  return response.json();
}

export class LyricsSyncOffsetApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = "LyricsSyncOffsetApiError";
  }
}

export class SpotifySessionApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = "SpotifySessionApiError";
  }
}

const readSpotifyTokenResponse = async (response: Response): Promise<ITokenExpiryPair> => {
  if (!response.ok) {
    throw new SpotifySessionApiError(await response.text(), response.status);
  }

  return response.json();
};

export async function saveLyricsSyncOffset(
  spotifyId: string,
  syncOffsetSeconds: number
): Promise<void> {
  const response = await fetch(`${baseURL}/api/lyrics/sync-offset`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ spotifyId, syncOffsetSeconds })
  });

  if (!response.ok) {
    throw new LyricsSyncOffsetApiError("Cannot save lyrics synchronization", response.status);
  }
}

export async function spotifyGetCurrentToken(
  refreshToken: string | null
): Promise<ITokenExpiryPair | null> {
  // Session cookie - backend will auto-refresh if expired
  const response = await fetch(`${baseURL}/api/spotify/token`, {
    credentials: "include" // Send session cookie
  });

  return readSpotifyTokenResponse(response);
}

export function spotifyRefreshToken(): Promise<ITokenExpiryPair | null> {
  // No longer passing refresh_token as query param - using session cookie instead
  return fetch(`${baseURL}/api/spotify/refresh-token`, {
    credentials: "include" // Send session cookie
  }).then(readSpotifyTokenResponse);
}

export async function acceptUserTos(): Promise<Response> {
  return fetch(`${baseURL}/api/user/tos`, {
    method: "POST",
    credentials: "include"
  });
}

export async function deleteSession(): Promise<Response> {
  const response = await fetch(`${baseURL}/api/session`, {
    credentials: "include",
    method: "DELETE"
  });

  if (!response.ok) {
    throw new SpotifySessionApiError(await response.text(), response.status);
  }

  return response;
}
